import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Save, Download, Lightbulb, AlertCircle, CheckCircle, Code } from 'lucide-react';
import { apiAIFetch, apiFetch, getUserRole } from '../api';
import PhaseSection from './PhaseSection';
import PhaseDurationBar from './PhaseDurationBar';
import ActivityAddModal from './ActivityAddModal';
import PromptPreviewModal from './PromptPreviewModal';
import ModelSelector from './ModelSelector';
import QuestionFormatterPanel from './QuestionFormatterPanel';

/**
 * PlayPracticePlanBuilder - Main builder component for Play-Practice-Play session plans
 *
 * Features:
 * - Auto-generates plan on student selection
 * - Three-phase structure (20-50-20 minutes)
 * - Drag-and-drop activity reordering
 * - Inline editing
 * - Save and export functionality
 */
const PlayPracticePlanBuilder = ({
  studentUuid,
  studentName,
  insights,
  onPlanGenerated
}) => {
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addModalPhase, setAddModalPhase] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'success' | 'error' | null
  const [showPromptModal, setShowPromptModal] = useState(false);
  const [promptData, setPromptData] = useState(null);
  const [loadingPrompt, setLoadingPrompt] = useState(false);
  const [selectedModel, setSelectedModel] = useState('gemini-2.5-flash');

  // Check if user is admin
  const isAdmin = getUserRole() === 'admin';

  // Toast helper
  const showToast = (message) => {
    window.dispatchEvent(new CustomEvent('app-toast', { detail: message }));
  };

  // Generate plan when student changes
  const generatePlan = useCallback(async () => {
    if (!studentUuid) {
      setPlan(null);
      return;
    }

    setLoading(true);
    setError(null);
    setSaveStatus(null);

    try {
      const response = await apiAIFetch('/api/suggest-plan-ppp', 'session_plan.suggest_ppp', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_uuid: studentUuid, model: selectedModel })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate plan');
      }

      const data = await response.json();
      const normalizedPlan = data.plan ? { ...data.plan } : data.plan;
      ['play_1', 'practice', 'play_2'].forEach((phase) => {
        const phaseData = normalizedPlan?.[phase];
        if (!phaseData || !Array.isArray(phaseData.activities)) return;
        normalizedPlan[phase] = {
          ...phaseData,
          activities: phaseData.activities.map((activity, idx) => ({
            ...activity,
            id: activity.id || `${phase}-${idx}-${activity.title || activity.duration_minutes || idx}`
          }))
        };
      });
      setPlan(normalizedPlan);
      setHasUnsavedChanges(false);

      if (onPlanGenerated) {
        onPlanGenerated(data);
      }
    } catch (err) {
      console.error('Error generating plan:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [studentUuid, selectedModel, onPlanGenerated]);

  // Auto-generate on student change
  useEffect(() => {
    if (studentUuid) {
      generatePlan();
    } else {
      setPlan(null);
    }
  }, [studentUuid, generatePlan]);

  // Activity management handlers
  const handleActivityUpdate = (activityId, updates) => {
    setPlan(prev => {
      if (!prev) return prev;
      const newPlan = { ...prev };

      for (const phaseKey of ['play_1', 'practice', 'play_2']) {
        if (newPlan[phaseKey]?.activities) {
          const idx = newPlan[phaseKey].activities.findIndex(a => a.id === activityId);
          if (idx !== -1) {
            newPlan[phaseKey] = {
              ...newPlan[phaseKey],
              activities: newPlan[phaseKey].activities.map((a, i) =>
                i === idx ? { ...a, ...updates } : a
              )
            };
            break;
          }
        }
      }

      return newPlan;
    });
    setHasUnsavedChanges(true);
    setSaveStatus(null);
  };

  const handleActivityRemove = (activityId) => {
    setPlan(prev => {
      if (!prev) return prev;
      const newPlan = { ...prev };

      for (const phaseKey of ['play_1', 'practice', 'play_2']) {
        if (newPlan[phaseKey]?.activities) {
          const activities = newPlan[phaseKey].activities.filter(a => a.id !== activityId);
          if (activities.length !== newPlan[phaseKey].activities.length) {
            newPlan[phaseKey] = {
              ...newPlan[phaseKey],
              activities: activities.map((a, i) => ({ ...a, order: i }))
            };
            break;
          }
        }
      }

      return newPlan;
    });
    setHasUnsavedChanges(true);
    setSaveStatus(null);
  };

  const handleActivityMove = (activityId, fromPhase, toPhase, toIndex) => {
    setPlan(prev => {
      if (!prev) return prev;
      const newPlan = { ...prev };

      // Find and remove the activity from source phase
      let movedActivity = null;
      if (newPlan[fromPhase]?.activities) {
        const idx = newPlan[fromPhase].activities.findIndex(a => a.id === activityId);
        if (idx !== -1) {
          movedActivity = newPlan[fromPhase].activities[idx];
          newPlan[fromPhase] = {
            ...newPlan[fromPhase],
            activities: newPlan[fromPhase].activities.filter(a => a.id !== activityId)
          };
        }
      }

      if (!movedActivity) return prev;

      // Insert into target phase at specified index
      if (newPlan[toPhase]?.activities) {
        const targetActivities = [...newPlan[toPhase].activities];
        const insertIndex = Math.min(toIndex, targetActivities.length);
        targetActivities.splice(insertIndex, 0, movedActivity);
        newPlan[toPhase] = {
          ...newPlan[toPhase],
          activities: targetActivities.map((a, i) => ({ ...a, order: i }))
        };
      }

      // Renumber source phase
      if (newPlan[fromPhase]?.activities) {
        newPlan[fromPhase] = {
          ...newPlan[fromPhase],
          activities: newPlan[fromPhase].activities.map((a, i) => ({ ...a, order: i }))
        };
      }

      return newPlan;
    });
    setHasUnsavedChanges(true);
    setSaveStatus(null);
  };

  const handleAddActivity = (phase, activity) => {
    setPlan(prev => {
      if (!prev) return prev;
      const newPlan = { ...prev };

      if (newPlan[phase]?.activities) {
        const activities = [...newPlan[phase].activities, activity];
        newPlan[phase] = {
          ...newPlan[phase],
          activities: activities.map((a, i) => ({ ...a, order: i }))
        };
      }

      return newPlan;
    });
    setHasUnsavedChanges(true);
    setSaveStatus(null);
  };

  // Save plan
  const handleSave = async () => {
    if (!plan || !studentUuid) return;

    setSaving(true);
    setSaveStatus(null);

    try {
      // Flatten activities for storage
      const allActivities = [
        ...(plan.play_1?.activities || []).map(a => ({ ...a, phase: 'play_1' })),
        ...(plan.practice?.activities || []).map(a => ({ ...a, phase: 'practice' })),
        ...(plan.play_2?.activities || []).map(a => ({ ...a, phase: 'play_2' }))
      ];

      const response = await apiFetch('/api/session-plans', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          student_id: studentUuid,
          goal: plan.session_focus || 'Play-Practice-Play Session',
          duration_minutes: 90,
          format: 'play-practice-play',
          phases: {
            play_1: plan.play_1,
            practice: plan.practice,
            play_2: plan.play_2
          },
          activities: allActivities,
          notes: '',
          tags: ['play-practice-play'],
          source: 'ai-assisted',
          personalization_tips: plan.personalization_tips || [],
          success_indicators: plan.success_indicators || []
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save plan');
      }

      setHasUnsavedChanges(false);
      setSaveStatus('success');
      showToast('Plan saved successfully!');
    } catch (err) {
      console.error('Error saving plan:', err);
      setSaveStatus('error');
      showToast('Failed to save plan');
    } finally {
      setSaving(false);
    }
  };

  // Export plan as text
  const handleExport = () => {
    if (!plan) return;

    const formatActivities = (activities) => {
      return activities.map((act, i) =>
        `   ${i + 1}. ${act.title} (${act.duration_minutes} min)\n      ${act.description}${act.neuro_technique ? `\n      Technique: ${act.neuro_technique}` : ''}`
      ).join('\n');
    };

    const planText = `
SESSION PLAN: Play-Practice-Play (90 minutes)
Student: ${studentName || 'Student'}
Generated: ${new Date().toLocaleDateString()}
${plan.session_focus ? `\nFocus: ${plan.session_focus}` : ''}

═══════════════════════════════════════════════════════════
PLAY 1 - OPENING (Target: 20 min)
═══════════════════════════════════════════════════════════
${formatActivities(plan.play_1?.activities || [])}

═══════════════════════════════════════════════════════════
PRACTICE - CORE LEARNING (Target: 50 min)
═══════════════════════════════════════════════════════════
${formatActivities(plan.practice?.activities || [])}

═══════════════════════════════════════════════════════════
PLAY 2 - CLOSING (Target: 20 min)
═══════════════════════════════════════════════════════════
${formatActivities(plan.play_2?.activities || [])}

${plan.personalization_tips?.length ? `
───────────────────────────────────────────────────────────
PERSONALIZATION TIPS:
${plan.personalization_tips.map(tip => `• ${tip}`).join('\n')}
` : ''}
${plan.success_indicators?.length ? `
───────────────────────────────────────────────────────────
SUCCESS INDICATORS:
${plan.success_indicators.map(ind => `• ${ind}`).join('\n')}
` : ''}

───────────────────────────────────────────────────────────
Generated with L.R. Brain Solutions Session Planner
    `.trim();

    const blob = new Blob([planText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `session-plan-ppp-${Date.now()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    showToast('Plan exported');
  };

  // View LLM prompt (admin only)
  const handleViewPrompt = async () => {
    if (!studentUuid || !isAdmin) return;

    setLoadingPrompt(true);

    try {
      const response = await apiFetch('/api/suggest-plan-ppp/preview-prompt', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ student_uuid: studentUuid })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch prompt preview');
      }

      const data = await response.json();
      setPromptData(data);
      setShowPromptModal(true);
    } catch (err) {
      console.error('Error fetching prompt preview:', err);
      showToast('Failed to load prompt preview');
    } finally {
      setLoadingPrompt(false);
    }
  };

  // Render empty state
  if (!studentUuid) {
    return (
      <div className="ppp-builder ppp-builder--empty">
        <div className="ppp-builder__empty-state">
          <div className="ppp-builder__empty-icon">📋</div>
          <h3>Select a Student</h3>
          <p>Choose a student above to auto-generate a personalized Play-Practice-Play session plan.</p>
        </div>
      </div>
    );
  }

  // Render loading state
  if (loading) {
    return (
      <div className="ppp-builder ppp-builder--loading">
        <div className="lrbrain-typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <p className="ppp-builder__loading-text">Generating personalized plan...</p>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="ppp-builder ppp-builder--error">
        <AlertCircle size={32} />
        <p>{error}</p>
        <button onClick={generatePlan} className="sp-btn sp-btn--primary">
          <RefreshCw size={14} />
          Try Again
        </button>
      </div>
    );
  }

  // Render plan builder
  return (
    <div className="ppp-builder">
      {/* Header */}
      <div className="ppp-builder__header">
        <div className="ppp-builder__header-left">
          <h2 className="ppp-builder__title">Session Plan</h2>
          {plan?.session_focus && (
            <p className="ppp-builder__focus">{plan.session_focus}</p>
          )}
        </div>
        <div className="ppp-builder__header-right">
          {isAdmin && (
            <button
              onClick={handleViewPrompt}
              disabled={loadingPrompt}
              className="sp-btn sp-btn--ghost sp-btn--sm"
              title="View LLM Prompt (Admin)"
            >
              <Code size={14} />
              {loadingPrompt ? 'Loading...' : 'View Prompt'}
            </button>
          )}
          <ModelSelector
            value={selectedModel}
            onChange={setSelectedModel}
            disabled={loading}
          />
          <button
            onClick={generatePlan}
            className="sp-btn sp-btn--outline sp-btn--sm"
            title="Regenerate plan"
          >
            <RefreshCw size={14} />
            Regenerate
          </button>
        </div>
      </div>

      <QuestionFormatterPanel />

      {/* Duration Bar */}
      {plan && <PhaseDurationBar phases={plan} totalDuration={90} />}

      {/* Phase Sections */}
      {plan && (
        <div className="ppp-builder__phases">
          <PhaseSection
            phaseKey="play_1"
            label={plan.play_1?.label || 'Play 1 - Opening'}
            targetDuration={plan.play_1?.target_duration || 20}
            activities={plan.play_1?.activities || []}
            onActivityMove={handleActivityMove}
            onActivityUpdate={handleActivityUpdate}
            onActivityRemove={handleActivityRemove}
            onAddActivity={(phase) => setAddModalPhase(phase)}
          />

          <PhaseSection
            phaseKey="practice"
            label={plan.practice?.label || 'Practice - Core Learning'}
            targetDuration={plan.practice?.target_duration || 50}
            activities={plan.practice?.activities || []}
            onActivityMove={handleActivityMove}
            onActivityUpdate={handleActivityUpdate}
            onActivityRemove={handleActivityRemove}
            onAddActivity={(phase) => setAddModalPhase(phase)}
          />

          <PhaseSection
            phaseKey="play_2"
            label={plan.play_2?.label || 'Play 2 - Closing'}
            targetDuration={plan.play_2?.target_duration || 20}
            activities={plan.play_2?.activities || []}
            onActivityMove={handleActivityMove}
            onActivityUpdate={handleActivityUpdate}
            onActivityRemove={handleActivityRemove}
            onAddActivity={(phase) => setAddModalPhase(phase)}
          />
        </div>
      )}

      {/* Personalization Tips */}
      {plan?.personalization_tips?.length > 0 && (
        <div className="ppp-builder__tips">
          <div className="ppp-builder__tips-header">
            <Lightbulb size={16} />
            <h4>Personalization Tips</h4>
          </div>
          <ul className="ppp-builder__tips-list">
            {plan.personalization_tips.map((tip, idx) => (
              <li key={idx}>{tip}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Success Indicators */}
      {plan?.success_indicators?.length > 0 && (
        <div className="ppp-builder__indicators">
          <div className="ppp-builder__indicators-header">
            <CheckCircle size={16} />
            <h4>Success Indicators</h4>
          </div>
          <ul className="ppp-builder__indicators-list">
            {plan.success_indicators.map((indicator, idx) => (
              <li key={idx}>{indicator}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Action Footer */}
      <div className="ppp-builder__footer">
        <div className="ppp-builder__footer-status">
          {hasUnsavedChanges && (
            <span className="ppp-builder__unsaved">Unsaved changes</span>
          )}
          {saveStatus === 'success' && (
            <span className="ppp-builder__saved">
              <CheckCircle size={14} /> Saved
            </span>
          )}
        </div>
        <div className="ppp-builder__footer-actions">
          <button
            onClick={handleExport}
            disabled={!plan}
            className="sp-btn sp-btn--outline"
          >
            <Download size={14} />
            Export
          </button>
          <button
            onClick={handleSave}
            disabled={!plan || saving}
            className="sp-btn sp-btn--primary"
          >
            <Save size={14} />
            {saving ? 'Saving...' : 'Save Plan'}
          </button>
        </div>
      </div>

      {/* Add Activity Modal */}
      {addModalPhase && (
        <ActivityAddModal
          phase={addModalPhase}
          onAdd={handleAddActivity}
          onClose={() => setAddModalPhase(null)}
        />
      )}

      {/* Prompt Preview Modal (Admin Only) */}
      {showPromptModal && promptData && (
        <PromptPreviewModal
          data={promptData}
          onClose={() => {
            setShowPromptModal(false);
            setPromptData(null);
          }}
        />
      )}
    </div>
  );
};

export default PlayPracticePlanBuilder;
